import {
  Body,
  Button,
  Icon,
  Input,
  useToast,
} from "@elaniintc/goes-design-system";
import { useUpdateEntityStatus } from "@/queries/digital-files/entities.api";
import { Link, useSearchParams } from "react-router";
import { ConfirmationModalBase } from "@/components/common/modals";
import { useState } from "react";
import type { EntityType } from "@/schemas/digital-files";
import { EntityStatusBadge } from "./components/EntityBadge";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { EntityActions } from "./components/EntityActions";
import Pagination from "@/components/common/table/pagination";
import { EntitySelector } from "./components/EntitySelector";
import {
  filterTypesEnum,
  type FilterTypeList,
  type SortOptionsTypes,
} from "@/types/common/filters";
import Filter from "@/components/ui/filters";
import SortDropdown from "../procesos/components/SortDropdown";
import { usePageSortFilters } from "@/hooks/use-page-fs";
import { usePagination } from "@/hooks/usePagination";
import { useGetRegistries } from "@/queries/digital-files/registries.api";
import { RegistryRowCard } from "./components/RegistryRow";
import type { EntityModalState } from "./types";
import { getEntityOperationModal } from "./utils";

const sortFields: SortOptionsTypes[] = [
  { label: "Nombre", value: "name" },
  { label: "Fecha de creación", value: "created_at" },
  { label: "Fecha de actualización", value: "updated_at" },
];

const FilterFields: FilterTypeList[] = [
  {
    label: "Fecha de creación",
    type: filterTypesEnum.dates,
    field: "created_at",
  },
  {
    label: "Última Actualización",
    type: filterTypesEnum.dates,
    field: "updated_at",
  },
  {
    label: "Estado",
    type: filterTypesEnum.status,
    field: "status",
    options: [
      { label: "Borrador", field: "DRAFT" },
      { label: "Activos", field: "ACTIVE" },
      { label: "Inactivo", field: "INACTIVE" },
    ],
  },
];

const DigitalFilesPage = () => {
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions.expedient_base_entities.create);
  const { mutateAsync: updateEntityStatus, isPending } =
    useUpdateEntityStatus();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entity, setselectedEntity] = useState<EntityType | null>(null);
  const [modalState, setModalState] = useState<EntityModalState>("idle");
  const {
    setPage,
    orderField,
    orderDirection,
    filters,
    selectedEntities,
    setSelectedEntities,
    handleApplySort,
    handleFilterChange,
    searchInput,
    setSearchInput,
    query,
  } = usePageSortFilters();
  const isInactive = entity?.status?.toLowerCase() === "inactive";

  const { data, isLoading } = useGetRegistries(entity?.id, query, !isInactive);
  const pagination = usePagination({
    meta: data?.meta,
    links: data?.links,
    onPageUpdate: (newPage) => {
      setPage(newPage);
    },
  });

  const handleEntityChange = (newEntity: EntityType) => {
    setselectedEntity(newEntity);
    setSearchParams({ entity: newEntity.id });
  };

  const handleConfirmation = async () => {
    if (!entity) return;
    if (modalState === "activate") {
      await confirmActivate(entity.id);
    }
    if (modalState === "deactivate") {
      await confirmDeactivate(entity.id);
      setModalState("idle");
    }
    if (modalState === "reactivate") {
      await confirmActivate(entity.id);
      setModalState("idle");
    }
  };

  const confirmActivate = async (id: string) => {
    try {
      const updatedEntity = await updateEntityStatus({ id, activate: true });
      setselectedEntity(updatedEntity.data);
      showToast({
        title: "Entidad activada",
        description: "La entidad ha sido activada correctamente.",
        options: {
          type: "success",
        },
      });
      setModalState("idle");
    } catch (e) {
      console.error("Error activating entity:", e);
      showToast({
        title: "Error al activar",
        description: "No se pudo activar la entidad.",
        options: {
          type: "error",
        },
      });
    }
  };

  const confirmDeactivate = async (id: string) => {
    try {
      const updatedEntity = await updateEntityStatus({ id, activate: false });
      setselectedEntity(updatedEntity.data);
      showToast({
        title: "Entidad desactivada",
        description: "La entidad ha sido desactivada correctamente.",
        options: {
          type: "success",
        },
      });
    } catch (e) {
      console.error("Error deactivating entity:", e);
      showToast({
        title: "Entidad desactivada",
        description: "No se pudo desactivar la entidad.",
        options: {
          type: "error",
        },
      });
    }
  };

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState !== "idle"}
        onClose={() => {
          setModalState("idle");
        }}
        {...getEntityOperationModal(modalState, entity?.version || 0)}
        onAccept={handleConfirmation}
        cancelText="Cancelar"
        acceptVariant="default"
        isLoading={isPending}
      />
      <div className="flex flex-col gap-4">
        <Body>Visualiza expedientes asociados a entidades</Body>
        <>
          <div className="flex flex-col gap-2 xs:flex-row justify-between">
            <div className="flex space-x-3">
              <SortDropdown
                currentOrderField={orderField}
                currentOrderDirection={orderDirection}
                sortOptions={sortFields}
                onClick={handleApplySort}
              />
              <Filter
                filters={FilterFields}
                currentFilters={filters}
                onCurrentFiltersChange={handleFilterChange}
                selecteds={selectedEntities}
                onSetSelecteds={setSelectedEntities}
              />
            </div>
            <div className="flex flex-column gap-2 justify-end">
              <div className="relative flex-1">
                <Icon
                  type="far"
                  name="search"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-body"
                />
                <Input
                  variant="sm"
                  className="text-sm w-full font-normal flex-auto pl-9"
                  placeholder="Buscar..."
                  onChange={(e) => setSearchInput(e.target.value)}
                  value={searchInput}
                />
              </div>
              {canCreate && (
                <Link to="/expedientes/entidad">
                  <Button size="sm">
                    Nueva entidad <Icon type="far" name="plus" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <EntitySelector
                onEntityChange={handleEntityChange}
                selectedEntity={entity}
                selectedEntityId={searchParams.get("entity") || undefined}
              />
              {entity && (
                <div className="flex items-center gap-2">
                  <EntityStatusBadge entity={entity}>
                    {`v${entity.version} ${entity?.status_label ?? entity?.status}`}
                  </EntityStatusBadge>
                  <EntityActions
                    entity={entity}
                    onActivate={() => setModalState("activate")}
                    onDeactivate={() => setModalState("deactivate")}
                    onReactivate={() => setModalState("reactivate")}
                  />
                </div>
              )}
            </div>
            {isLoading ? (
              <div className="w-full h-32 flex items-center justify-center">
                <Icon
                  type="far"
                  name="spinner-third"
                  className="text-on-disabled animate-spin text-2xl"
                />
              </div>
            ) : (
              <>
                {data &&
                  data.data?.map((registry) => (
                    <RegistryRowCard
                      key={registry.id}
                      entityId={searchParams.get("entity")}
                      data={registry}
                    />
                  ))}
                <Pagination
                  classNameItem="min-w-8 w-fit"
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  pageSize={pagination.pageSize}
                  currentPage={pagination.currentPage}
                  hasNext={pagination.hasNextPage}
                  hasPrevious={pagination.hasPreviousPage}
                  onPageChange={pagination.onPageChange}
                  onNext={pagination.onNext}
                  onPrevious={pagination.onPrevious}
                />
              </>
            )}
          </div>
        </>
      </div>
    </>
  );
};

export default DigitalFilesPage;
